import "./theme.scss";

import { initializeApp } from "@firebase/app";
import axios from "axios";
import { getAnalytics } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { createPinia, setActivePinia } from "pinia";
import { createPersistedState } from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import VueSocialSharing from "vue-social-sharing";

import vuetify from "@/plugins/vuetify";

// Create Pinia instance FIRST
const pinia = createPinia();
pinia.use(createPersistedState());
setActivePinia(pinia);

const sharedConstants = require("./shared.js").default;

import App from "./App.vue";
import router from "./router/router";
import { getUser } from "./services/controllers/userController";
import { useNonPersistentStore } from "./stores";
import { usePersistentStore } from "./stores";

let app: any = null;

// Suppress ResizeObserver loop errors
const originalError = console.error;
console.error = (...args: any[]) => {
  if (
    args[0] &&
    typeof args[0] === "string" &&
    args[0].includes("ResizeObserver loop completed with undelivered notifications")
  ) {
    return;
  }
  originalError.apply(console, args);
};

// Global error handler for ResizeObserver
window.addEventListener("error", (event) => {
  if (
    event.message &&
    event.message.includes("ResizeObserver loop completed with undelivered notifications")
  ) {
    event.preventDefault();
    return false;
  }
});

// Handle unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  if (
    event.reason &&
    event.reason.message &&
    event.reason.message.includes("ResizeObserver loop completed with undelivered notifications")
  ) {
    event.preventDefault();
    return false;
  }
});

const isToServer = (url: string): boolean => {
  const { apiBaseUrl } = sharedConstants;
  return url.includes(apiBaseUrl);
};

// shared constants
const { firestoreConfig } = sharedConstants;
sharedConstants.install = (app: any) => {
  app.config.globalProperties.$sharedConstants = sharedConstants;
};

// add token to axios
const axiosOriginalGet = axios.get;
const newAxiosGet = async (...urlParams: [string, ...any[]]): Promise<any> => {
  const urlParam = urlParams[0];
  // Lazy store access to avoid calling before Pinia is initialized
  const nonPersistentStore = useNonPersistentStore();
  const currentFirebaseUser = nonPersistentStore.currentFirebaseUser;
  if (currentFirebaseUser && isToServer(urlParam)) {
    const accessToken = await (currentFirebaseUser as any).getIdToken();
    if (urlParam.includes("?")) {
      urlParams[0] = `${urlParam}&authToken=${accessToken}`;
      if (sharedConstants?.socket?.id) {
        urlParams[0] += `&sid=${sharedConstants.socket.id}`;
      }
    } else {
      urlParams[0] = `${urlParam}?authToken=${accessToken}`;
      if (sharedConstants?.socket?.id) {
        urlParams[0] += `&sid=${sharedConstants.socket.id}`;
      }
    }
  } else {
    urlParams[0] = urlParam;
  }
  const config = urlParams.length > 1 ? urlParams[1] : undefined;
  return await axiosOriginalGet(urlParams[0], config);
};
axios.get = newAxiosGet;

const axiosOriginalPatch = axios.patch;
const newAxiosPatch = async (...req: [string, any, ...any[]]): Promise<any> => {
  const nonPersistentStore = useNonPersistentStore();
  const currentFirebaseUser = nonPersistentStore.currentFirebaseUser;
  if (currentFirebaseUser) {
    const accessToken = await (currentFirebaseUser as any).getIdToken();
    if (sharedConstants?.socket?.id) {
      if (req[0].includes("?")) {
        req[0] += `&sid=${sharedConstants.socket.id}`;
      } else {
        req[0] += `?sid=${sharedConstants.socket.id}`;
      }
    }
    req[1].authToken = accessToken;
  }
  const config = req.length > 2 ? req[2] : undefined;
  return await axiosOriginalPatch(req[0], req[1], config);
};
axios.patch = newAxiosPatch;

const axiosOriginalPost = axios.post;
const newAxiosPost = async (...req: [string, any, ...any[]]): Promise<any> => {
  const nonPersistentStore = useNonPersistentStore();
  const currentFirebaseUser = nonPersistentStore.currentFirebaseUser;
  if (currentFirebaseUser) {
    const accessToken = await (currentFirebaseUser as any).getIdToken();
    if (sharedConstants?.socket?.id) {
      if (req[0].includes("?")) {
        req[0] += `&sid=${sharedConstants.socket.id}`;
      } else {
        req[0] += `?sid=${sharedConstants.socket.id}`;
      }
    }
    req[1].authToken = accessToken;
  }
  const config = req.length > 2 ? req[2] : undefined;
  return await axiosOriginalPost(req[0], req[1], config);
};
axios.post = newAxiosPost;

const axiosOriginalDelete = axios.delete;
const newAxiosDelete = async (...req: [string, any, ...any[]]): Promise<any> => {
  const nonPersistentStore = useNonPersistentStore();
  const currentFirebaseUser = nonPersistentStore.currentFirebaseUser;
  if (currentFirebaseUser) {
    const accessToken = await (currentFirebaseUser as any).getIdToken();
    if (sharedConstants?.socket?.id) {
      if (req[0].includes("?")) {
        req[0] += `&sid=${sharedConstants.socket.id}`;
      } else {
        req[0] += `?sid=${sharedConstants.socket.id}`;
      }
    }
    req[1].authToken = accessToken;
    req[1] = { data: req[1] };
  }
  return await axiosOriginalDelete(req[0], req[1]);
};
axios.delete = newAxiosDelete;

initializeApp(firestoreConfig);
getAnalytics();

getAuth().onAuthStateChanged((authImpl: any) => {
  const auth = authImpl && authImpl.auth ? authImpl.auth : undefined;
  const user = auth ? auth.currentUser : undefined;

  if (user) {
    (user as any).getIdToken(true);
  }

  // first occurence of app being loaded in a session
  if (!app) {
    // Create Vue 3 app
    app = createApp(App);

    // Install plugins
    app.use(pinia);
    app.use(router);
    app.use(vuetify);
    app.use(VueSocialSharing);
    app.use(sharedConstants);

    // Mount the app
    app.mount("#app");

    // Now that Pinia is initialized, access the stores
    const nonPersistentStore = useNonPersistentStore();
    const persistentStore = usePersistentStore();

    nonPersistentStore.setCurrentFirebaseUser(user);

    const currentlyLoggedInBand = nonPersistentStore.currentlyLoggedInBand;
    if (currentlyLoggedInBand && (currentlyLoggedInBand as any).firestore) {
      nonPersistentStore.setCurrentlyLoggedInBand(null);
      (persistentStore as any).setCurrentlyLoggedInBand(null);
    }

    const currencyOptionFrompersistentStore = persistentStore.selectedCurrency;
    if (currencyOptionFrompersistentStore) {
      nonPersistentStore.setSelectedCurrency(currencyOptionFrompersistentStore);
    }

    // Restore currentlyLoggedInBand from persistent store
    const persistentBand = (persistentStore as any).currentlyLoggedInBandParsed;
    if (persistentBand) {
      nonPersistentStore.setCurrentlyLoggedInBand(persistentBand);
    }

    // see if there is already a unique id string in the persistent store
    const uniqueId = persistentStore.uniqueId;
    // if not, generate a new one and save it
    if (!uniqueId) {
      const newUniqueId = Math.random().toString(36).substring(2, 15);
      nonPersistentStore.setUniqueId(newUniqueId);
    } else {
      nonPersistentStore.setUniqueId(uniqueId);
    }

    (window as any).analytics.identify(uniqueId);

    // see if there is a customerEmail in the persistent store
    const customerEmail = persistentStore.customerEmail;
    if (customerEmail) {
      nonPersistentStore.setCustomerEmail(customerEmail);
    }
    const localSongPasses = persistentStore.localSongPasses;
    if (localSongPasses) {
      nonPersistentStore.setLocalSongPasses(localSongPasses);
    }
    const hasBeenAskedForAge = persistentStore.hasBeenAskedForAge;
    if (hasBeenAskedForAge) {
      const isOver18 = persistentStore.isOver18;
      nonPersistentStore.setIsOver18(isOver18);
    }
    const optIntoEmails = persistentStore.optIntoEmails;
    if (optIntoEmails) {
      nonPersistentStore.setOptIntoEmails(optIntoEmails);
    }
    const hasOptedIntoEmails = persistentStore.hasOptedIntoEmails;
    if (hasOptedIntoEmails) {
      nonPersistentStore.setHasOptedIntoEmails(hasOptedIntoEmails);
    }
    const customerName = persistentStore.customerName;
    if (customerName) {
      nonPersistentStore.setCustomerName(customerName);
    }
    // see if any past gig stats exist
    const pastGigStats = persistentStore.userVotingForGigs;
    if (pastGigStats && Object.keys(pastGigStats).length > 0) {
      nonPersistentStore.setUserVotingForGigs(pastGigStats);
    }
    if (user) {
      getUser({ uid: user.uid });
    }
  } else {
    // For subsequent auth state changes, just update the user
    const nonPersistentStore = useNonPersistentStore();
    nonPersistentStore.setCurrentFirebaseUser(user);
  }
});
